import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { videoAPI, authAPI } from '../lib/api';
import { Button } from './shadcn/button';
import { Card, CardContent } from './shadcn/card';
import { Alert, AlertDescription } from './shadcn/alert';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle, ChevronUp, ChevronDown, Loader2, X } from 'lucide-react';
import UploadStepper from './upload/UploadStepper';
import TimestampModal from './upload/TimestampModal';
import FileSelectionStep from './upload/FileSelectionStep';
import TimestampDetectionStep from './upload/TimestampDetectionStep';
import SessionOrganizationStep from './upload/SessionOrganizationStep';
import ReportConfigurationStep from './upload/ReportConfigurationStep';
import ProcessingSummaryStep from './upload/ProcessingSummaryStep';
import { 
  generateThumbnail, 
  generateThumbnailAtTime, 
  restoreFirstFrameThumbnail,
  getVideoDuration
} from './upload/videoUtils';
import { 
  performOCR, 
} from './upload/ocrUtils';
import { 
  checkTimestampContinuity,
  getGroupTimestampRange,
  getGroupTotalDuration,
  formatDuration,
  getOverallTimestampRange,
  getTotalDuration,
  addToGroupBasedOnTimestamp,
  sortVideosInGroups
} from './upload/groupingUtils';
import { uploadFileInChunks, resumeUpload } from '../lib/chunkedUpload';

// Steps configuration
const steps = ['Files', 'Timestamps', 'Sessions', 'Report', 'Summary'];

function UploadPage() {
  // State for video thumbnails
  const [videoThumbnails, setVideoThumbnails] = useState({});
  
  // Step state
  const [step, setStep] = useState(1);
  const [visitedSteps, setVisitedSteps] = useState([1]);
  
  // Reset confirmation state
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [resetTargetStep, setResetTargetStep] = useState(1);
  
  // Debug step changes
  useEffect(() => {
  }, [step]);
  
  // Check for form submissions
  useEffect(() => {
    const form = document.getElementById('upload-form');
    if (form) {
      const handleSubmitEvent = (e) => {
      };
      
      form.addEventListener('submit', handleSubmitEvent);
      
      return () => {
        form.removeEventListener('submit', handleSubmitEvent);
      };
    }
  }, []);
  const [videoFiles, setVideoFiles] = useState([]);
  
  // Timestamps state
  const [timestamps, setTimestamps] = useState({});
  
  // Unified timestamps state (combines OCR extracted and manually entered timestamps)
  const [unifiedTimestamps, setUnifiedTimestamps] = useState({});
  
  // OCR results state
  const [ocrResults, setOcrResults] = useState({});
  const [ocrProcessing, setOcrProcessing] = useState({});
  const [ocrCompleted, setOcrCompleted] = useState({}); // Track completed OCR processes
  const [extractedTimestamps, setExtractedTimestamps] = useState({});
  const [ocrRetryCount, setOcrRetryCount] = useState({}); // Track retry attempts per file
  const [ocrProgress, setOcrProgress] = useState({}); // Track OCR progress per file
  const [ocrLog, setOcrLog] = useState([]); // Track OCR processing logs
  
  // Clear OCR log on component mount (when page loads)
  useEffect(() => {
    setOcrLog([]);
  }, []);
  
  // Video grouping state
  const [videoGroups, setVideoGroups] = useState([]); // Store video similarity groups
  const [groupingInProgress, setGroupingInProgress] = useState(false); // Track grouping process
  const [groupingCompleted, setGroupingCompleted] = useState(false); // Track if grouping is done
  const [locationNames, setLocationNames] = useState({}); // Store location names for each group
  const [draggedVideo, setDraggedVideo] = useState(null); // Track currently dragged video
  const [sourceGroupId, setSourceGroupId] = useState(null); // Track source group of dragged video
  const [draggedGroup, setDraggedGroup] = useState(null); // Track currently dragged group
  const [manualTimestampRanges, setManualTimestampRanges] = useState({}); // Store manually edited timestamp ranges
  const [videoDurations, setVideoDurations] = useState({}); // Store video durations
  const [confirmedDurations, setConfirmedDurations] = useState({}); // Store confirmed duration status for groups
  const [allDurationsConfirmed, setAllDurationsConfirmed] = useState(false); // Track if all durations are confirmed
  const [manualStartTimes, setManualStartTimes] = useState({}); // Store manually entered start times for videos without timestamps
  const [enlargedThumbnail, setEnlargedThumbnail] = useState(null); // Store data for enlarged thumbnail modal
  const [timestampSaved, setTimestampSaved] = useState(false); // Track if timestamp was saved successfully

  // Update allDurationsConfirmed when confirmedDurations changes
  useEffect(() => {
    if (videoGroups.length > 0) {
      const allConfirmed = videoGroups.every(group => confirmedDurations[group.id]);
      setAllDurationsConfirmed(allConfirmed);
    } else if (videoFiles.length === 1) {
      // For single video file, check if duration is confirmed
      const isConfirmed = confirmedDurations[1] || false;
      setAllDurationsConfirmed(isConfirmed);
    } else {
      setAllDurationsConfirmed(false);
    }
  }, [confirmedDurations, videoGroups.length, videoFiles.length]); // Use videoGroups.length instead of videoGroups to avoid reference changes
  
  // Update unified timestamps when extracted timestamps or manual timestamps change
  useEffect(() => {
    const newUnifiedTimestamps = {};
    
    // Add OCR extracted timestamps
    Object.keys(extractedTimestamps).forEach(fileId => {
      newUnifiedTimestamps[fileId] = extractedTimestamps[fileId];
    });
    
    // Add manually entered timestamps
    Object.keys(manualStartTimes).forEach(key => {
      // Check if key is a file ID (number) or a group ID (special case)
      if (!isNaN(key)) {
        // This is a file ID
        const fileId = parseInt(key);
        if (!newUnifiedTimestamps[fileId]) {
          newUnifiedTimestamps[fileId] = [manualStartTimes[key]];
        } else {
          // Merge with existing timestamps
          newUnifiedTimestamps[fileId] = [...newUnifiedTimestamps[fileId], manualStartTimes[key]];
        }
      }
    });
    
    setUnifiedTimestamps(newUnifiedTimestamps);
  }, [extractedTimestamps, manualStartTimes]);
  
  // Confirm all durations
  const confirmAllDurations = () => {
    // Check if directions have been set for all groups
    let allDirectionsSet = true;
    if (videoFiles.length > 1) {
      allDirectionsSet = videoGroups.every(group => groupDirectionsSet[group.id]);
    } else if (videoFiles.length === 1) {
      allDirectionsSet = groupDirectionsSet[1] || false;
    }
    
    if (!allDirectionsSet) {
      // Show directions error overlay instead of regular error
      setShowDirectionsErrorOverlay(true);
      return;
    }
    
    const newConfirmedDurations = {};
    if (videoFiles.length > 1) {
      videoGroups.forEach(group => {
        newConfirmedDurations[group.id] = true;
      });
    } else if (videoFiles.length === 1) {
      newConfirmedDurations[1] = true;
    }
    setConfirmedDurations(newConfirmedDurations);
  };
  
  // Form data state
  const [formData, setFormData] = useState({
    location: '',
    reportInterval: 15, // Default to 15 minutes (number instead of string)
    reportClasses: {
      // Main classes
      passengerVehicle: true,
      threeAxelTruck: true,
      bicycle: true,
      pedestrians: true,
      // Sub-classes (optional)
      commercialTruck: false,
      bus: false
    },
    reportMode: 'same' // 'same' or 'separate'
  });
  
  // State for separate configurations for each location
  const [separateConfigurations, setSeparateConfigurations] = useState({});
  
  // State for directions set for each group
  const [groupDirectionsSet, setGroupDirectionsSet] = useState({});
  
  // State for cardinal directions for each group
  const [groupCardinalDirections, setGroupCardinalDirections] = useState({});
  
  // State for saved drivable areas for each group
  const [savedDrivableAreas, setSavedDrivableAreas] = useState({});
  
  // State for showing directions error overlay
  const [showDirectionsErrorOverlay, setShowDirectionsErrorOverlay] = useState(false);
  
  // UI state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(false);
  
  const { currentUser, deletionStatus } = useAuth();
  const navigate = useNavigate();

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFiles = (files) => {
    // Check if files is defined and is an array or FileList
    if (!files || (files.length === undefined && !Array.isArray(files))) {
      setError('No valid files provided');
      setProcessingFiles(false);
      return;
    }
    
    const validFiles = [];
    let errorMessage = '';
    
    // Show processing state
    setProcessingFiles(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check if file exists and has required properties
      if (!file) {
        continue;
      }
      
      // Check if file is a video
      if (!file.type || !file.type.startsWith('video/')) {
        errorMessage = 'Please select valid video files (MP4, AVI, etc.)';
        continue;
      }
      
      // Check file size (limit to 500MB)
      if (file.size && file.size > 500 * 1024 * 1024) {
        errorMessage = 'File size exceeds 500MB limit';
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length > 0) {
      // Add unique IDs to files
      const filesWithIds = validFiles.map((file, index) => ({
        id: Date.now() + index, // Unique ID
        file: file
      }));
      
      setVideoFiles(prev => [...prev, ...filesWithIds]);
      
      // Add files to thumbnail generation queue
      setThumbnailQueue(prev => [...prev, ...filesWithIds]);
      
      if (errorMessage) {
        setError(errorMessage);
      } else {
        setError('');
      }
    } else if (errorMessage) {
      setError(errorMessage);
    }
    
    // Hide processing state after a short delay
    setTimeout(() => {
      setProcessingFiles(false);
    }, 500);
  };

  // Thumbnail generation queue
  const [thumbnailQueue, setThumbnailQueue] = useState([]);
  const [processingThumbnails, setProcessingThumbnails] = useState(false);
  
  // OCR auto-processing
  const [autoOcrTriggered, setAutoOcrTriggered] = useState(false);

  // Process thumbnail queue
  useEffect(() => {
    if (!processingThumbnails && thumbnailQueue.length > 0) {
      setProcessingThumbnails(true);
      const nextItem = thumbnailQueue[0];
      generateThumbnail(nextItem.id, nextItem.file, setVideoThumbnails, setThumbnailQueue, setProcessingThumbnails);
    }
  }, [thumbnailQueue, processingThumbnails]);

  // Auto OCR processing when thumbnails are ready
  useEffect(() => {
    if (step === 2 && !autoOcrTriggered && videoFiles.length > 0) {
      // Check if OCR has already been completed for all files
      const allOcrCompleted = videoFiles.every(item => ocrCompleted[item.id]);
      
      // Only initialize OCR process if it hasn't been completed for all files
      if (!allOcrCompleted) {
        // Add initial log entry
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: 'Initializing timestamp extraction process...'
        }]);
        
        // Perform OCR on all files automatically
        videoFiles.forEach(item => {
          if (!ocrProcessing[item.id] && !extractedTimestamps[item.id]?.length) {
            // Reset retry count and progress before starting OCR for this file
            setOcrRetryCount(prev => ({
              ...prev,
              [item.id]: 0
            }));
            setOcrProgress(prev => ({
              ...prev,
              [item.id]: { current: 0, total: 5, status: 'Preparing analysis...' }
            }));
            setOcrCompleted(prev => ({
              ...prev,
              [item.id]: false
            }));
            
            // Use a small delay for each file to prevent UI blocking
            setTimeout(() => {
              performOCR(item.id, videoFiles, setOcrProcessing, setOcrRetryCount, setOcrProgress, setOcrLog, setOcrResults, setExtractedTimestamps, setVideoThumbnails, generateThumbnailAtTime, setOcrCompleted);
            }, 0);
          }
        });
      }
      
      setAutoOcrTriggered(true);
    }
    
    // Reset auto OCR trigger when step changes
    if (step !== 2) {
      setAutoOcrTriggered(false);
    }
  }, [step, videoFiles.length, ocrProcessing, extractedTimestamps, autoOcrTriggered]);

  // Reset grouping state when step changes (but preserve groups)
  // Also trigger grouping when entering the sessions tab (step 3)
  useEffect(() => {
    if (step !== 2 && step !== 3) {
      setGroupingInProgress(false);
      // Only reset groupingCompleted if we're going back to step 1 AND there are no video files
      // This preserves the grouping information when navigating between steps 2-5
      if (step === 1 && videoFiles.length === 0) {
        setGroupingCompleted(false);
        setVideoGroups([]);
      }
      // Clean up any active drag state
      disableAutoScroll();
    }
    
    // Trigger grouping when entering the sessions tab (step 3) if not already completed and we have video files
    if (step === 3 && videoFiles.length > 0 && !groupingCompleted && !groupingInProgress) {
      // Check if all files have completed OCR processing
      const allOcrCompleted = videoFiles.every(item => ocrCompleted[item.id]);
      
      // Also check if we already have video groups (meaning grouping was done before)
      const hasExistingGroups = videoGroups.length > 0;
      
      if (allOcrCompleted && !hasExistingGroups) {
        // For single video file, we don't need grouping but we need to mark it as completed
        if (videoFiles.length === 1) {
          setGroupingCompleted(true);
          // Initialize location name for single video file
          if (!locationNames[1]) {
            setLocationNames(prev => ({
              ...prev,
              1: 'Location 1'
            }));
          }
        } else {
          // Trigger grouping process only if we don't already have groups
          performVideoGrouping();
        }
      } else if (hasExistingGroups && !groupingCompleted) {
        // If we already have groups, just mark grouping as completed
        // Only update state if it's not already true to prevent infinite loop
        setGroupingCompleted(true);
      }
    }
    
    // Return cleanup function
    return () => {
    };
  }, [step, videoFiles.length, groupingCompleted, groupingInProgress]); // Removed videoGroups from dependencies and use videoFiles.length instead

  // Auto-scroll variables
  let scrollInterval = null;
  let lastMouseEvent = null;
  const scrollSpeed = 10;
  const scrollThreshold = 100; // Increased threshold to match scroll indicator size

  // Track mouse position
  const handleMouseMove = (e) => {
    lastMouseEvent = e;
  };

  // Enable auto-scrolling while dragging
  const enableAutoScroll = () => {    
    if (scrollInterval) {
      return; // Already enabled
    }
    
    // Add mouse move listener
    document.addEventListener('mousemove', handleMouseMove);
    
    scrollInterval = setInterval(() => {
      if (!lastMouseEvent) {
        return;
      }
      
      const mouseY = lastMouseEvent.clientY;
      const windowHeight = window.innerHeight;      
      // Only show scroll indicators when dragging a video or group
      if (draggedVideo || draggedGroup) {
        // Show/hide scroll indicators
        const topIndicator = document.getElementById('scroll-top-indicator');
        const bottomIndicator = document.getElementById('scroll-bottom-indicator');
                
        // Scroll up if mouse is near top
        if (mouseY < scrollThreshold) {
          window.scrollBy(0, -scrollSpeed);
          if (topIndicator) {
            topIndicator.style.display = 'block';
            topIndicator.style.opacity = '1';
          }
          if (bottomIndicator) {
            bottomIndicator.style.display = 'none';
            bottomIndicator.style.opacity = '0';
          }
        }
        // Scroll down if mouse is near bottom
        else if (mouseY > windowHeight - scrollThreshold) {
          window.scrollBy(0, scrollSpeed);
          if (topIndicator) {
            topIndicator.style.display = 'none';
            topIndicator.style.opacity = '0';
          }
          if (bottomIndicator) {
            bottomIndicator.style.display = 'block';
            bottomIndicator.style.opacity = '1';
          }
        } else {
          // Hide both indicators when not scrolling
          if (topIndicator) {
            topIndicator.style.display = 'none';
            topIndicator.style.opacity = '0';
          }
          if (bottomIndicator) {
            bottomIndicator.style.display = 'none';
            bottomIndicator.style.opacity = '0';
          }
        }
      }
    }, 50);
  };

  // Disable auto-scrolling
  const disableAutoScroll = () => {
    // Remove mouse move listener
    document.removeEventListener('mousemove', handleMouseMove);
    lastMouseEvent = null;
    
    // Hide scroll indicators
    const topIndicator = document.getElementById('scroll-top-indicator');
    const bottomIndicator = document.getElementById('scroll-bottom-indicator');
    if (topIndicator) {
      topIndicator.style.display = 'none';
      topIndicator.style.opacity = '0';
    }
    if (bottomIndicator) {
      bottomIndicator.style.display = 'none';
      bottomIndicator.style.opacity = '0';
    }
    
    if (scrollInterval) {
      clearInterval(scrollInterval);
      scrollInterval = null;
    }
  };

  // Handle video drag start
  const handleVideoDragStart = (e, video, groupId) => {
    setDraggedVideo(video);
    setSourceGroupId(groupId);
    setDraggedGroup(null); // Ensure we're not dragging a group
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
    
    // Show scroll indicators initially when starting drag
    setTimeout(() => {
      const topIndicator = document.getElementById('scroll-top-indicator');
      const bottomIndicator = document.getElementById('scroll-bottom-indicator');
      if (topIndicator) {
        topIndicator.style.display = 'block';
        topIndicator.style.opacity = '1';
      }
      if (bottomIndicator) {
        bottomIndicator.style.display = 'block';
        bottomIndicator.style.opacity = '1';
      }
    }, 10);
    
    // Enable auto-scrolling while dragging
    enableAutoScroll();
  };

  // Handle drag end (cleanup)
  const handleDragEnd = () => {
    // Reset visual feedback
    disableAutoScroll();
  };

  // Handle group drag start
  const handleGroupDragStart = (e, groupId) => {
    setDraggedGroup(groupId);
    setDraggedVideo(null); // Ensure we're not dragging a video
    setSourceGroupId(null);
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback to the group header
    e.target.style.opacity = '0.7';
    
    // Show scroll indicators initially when starting drag
    setTimeout(() => {
      const topIndicator = document.getElementById('scroll-top-indicator');
      const bottomIndicator = document.getElementById('scroll-bottom-indicator');
      if (topIndicator) {
        topIndicator.style.display = 'block';
        topIndicator.style.opacity = '1';
      }
      if (bottomIndicator) {
        bottomIndicator.style.display = 'block';
        bottomIndicator.style.opacity = '1';
      }
    }, 10);
    
    // Enable auto-scrolling while dragging
    enableAutoScroll();
  };

  // Handle group drag over
  const handleGroupDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Add visual feedback to drop zones
    if (e.target.classList.contains('group-drop-zone') || e.target.closest('.group-drop-zone')) {
      const dropZone = e.target.classList.contains('group-drop-zone') 
        ? e.target 
        : e.target.closest('.group-drop-zone');
      dropZone.classList.add('ring-2', 'ring-primary', 'ring-opacity-50');
      
      // Add extra visual feedback when dragging a group
      if (draggedGroup) {
        dropZone.classList.add('bg-blue-50');
        // Show merge indicator
        const indicator = dropZone.querySelector('.group-drop-indicator');
        if (indicator) {
          indicator.classList.add('opacity-100');
          indicator.classList.remove('opacity-0');
        }
      }
    }
  };

  // Handle group drag leave
  const handleGroupDragLeave = (e) => {
    // Remove visual feedback from drop zones
    if (e.target.classList.contains('group-drop-zone') || e.target.closest('.group-drop-zone')) {
      const dropZone = e.target.classList.contains('group-drop-zone') 
        ? e.target 
        : e.target.closest('.group-drop-zone');
      dropZone.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50', 'bg-blue-50');
      
      // Hide merge indicator
      const indicator = dropZone.querySelector('.group-drop-indicator');
      if (indicator) {
        indicator.classList.remove('opacity-100');
        indicator.classList.add('opacity-0');
      }
    }
  };

  // Handle group drop
  const handleGroupDrop = (e, targetGroupId) => {
    e.preventDefault();
    
    // Remove visual feedback
    if (e.target.classList.contains('group-drop-zone') || e.target.closest('.group-drop-zone')) {
      const dropZone = e.target.classList.contains('group-drop-zone') 
        ? e.target 
        : e.target.closest('.group-drop-zone');
      dropZone.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50', 'bg-blue-50');
      
      // Hide merge indicator
      const indicator = dropZone.querySelector('.group-drop-indicator');
      if (indicator) {
        indicator.classList.remove('opacity-100');
        indicator.classList.add('opacity-0');
      }
    }
    
    // Disable auto-scroll
    disableAutoScroll();
    
    // Handle group dropping onto another group
    if (draggedGroup && draggedGroup !== targetGroupId) {
      // Merge the dragged group into the target group
      setVideoGroups(prevGroups => {
        const newGroups = [...prevGroups];
        
        // Find source and target groups
        const sourceGroupIndex = newGroups.findIndex(group => group.id === draggedGroup);
        const targetGroupIndex = newGroups.findIndex(group => group.id === targetGroupId);
        
        if (sourceGroupIndex !== -1 && targetGroupIndex !== -1) {
          // Merge videos from source group to target group
          newGroups[targetGroupIndex] = {
            ...newGroups[targetGroupIndex],
            files: [...newGroups[targetGroupIndex].files, ...newGroups[sourceGroupIndex].files]
          };
          
          // Remove the source group
          newGroups.splice(sourceGroupIndex, 1);
          
          // Renumber groups
          const renumberedGroups = newGroups.map((group, index) => ({
            ...group,
            id: index + 1
          }));
          
          // Update location names to match new group IDs
          const newLocationNames = {...locationNames};
          // Remove the location name for the deleted group
          delete newLocationNames[draggedGroup];
          // Update location names for renumbered groups
          newGroups.forEach((group, index) => {
            const newId = index + 1;
            if (group.id !== newId && locationNames[group.id]) {
              newLocationNames[newId] = locationNames[group.id];
              delete newLocationNames[group.id];
            }
          });
          
          setLocationNames(newLocationNames);
          return renumberedGroups;
        }
        
        return newGroups;
      });
      
      // Reset drag state
      setDraggedGroup(null);
      return;
    }
    
    // Handle video dropping onto a group (existing functionality)
    if (!draggedVideo || !sourceGroupId) return;
    
    // Prevent dropping onto the same group
    if (sourceGroupId === targetGroupId) {
      setDraggedVideo(null);
      setSourceGroupId(null);
      return;
    }
    
    // Update groups
    setVideoGroups(prevGroups => {
      const newGroups = [...prevGroups];
      
      // Remove video from source group
      const sourceGroupIndex = newGroups.findIndex(group => group.id === sourceGroupId);
      if (sourceGroupIndex !== -1) {
        newGroups[sourceGroupIndex] = {
          ...newGroups[sourceGroupIndex],
          files: newGroups[sourceGroupIndex].files.filter(file => file.id !== draggedVideo.id)
        };
      }
      
      // Add video to target group
      const targetGroupIndex = newGroups.findIndex(group => group.id === targetGroupId);
      if (targetGroupIndex !== -1) {
        newGroups[targetGroupIndex] = {
          ...newGroups[targetGroupIndex],
          files: [...newGroups[targetGroupIndex].files, draggedVideo]
        };
      }
      
      // Remove empty groups and renumber groups
      const nonEmptyGroups = newGroups.filter(group => group.files.length > 0);
      const renumberedGroups = nonEmptyGroups.map((group, index) => ({
        ...group,
        id: index + 1
      }));
      
      // Update location names to match new group IDs
      const newLocationNames = {...locationNames};
      nonEmptyGroups.forEach((group, index) => {
        const newId = index + 1;
        if (group.id !== newId && locationNames[group.id]) {
          newLocationNames[newId] = locationNames[group.id];
          delete newLocationNames[group.id];
        }
      });
      
      setLocationNames(newLocationNames);
      return renumberedGroups;
    });
    
    // Reset drag state
    setDraggedVideo(null);
    setSourceGroupId(null);
  };

  // Handle drop to create a new group
  const handleNewGroupDrop = (e) => {
    e.preventDefault();
    
    // Remove visual feedback from new group zone
    if (e.target.classList.contains('min-h-[100px]')) {
      e.target.classList.remove('bg-gray-200');
    }
    
    // Disable auto-scroll
    disableAutoScroll();
    
    // Handle group dropping to create a new group
    if (draggedGroup) {
      // This would be creating a new group from an existing group, which doesn't make sense
      // We'll just reset the drag state
      setDraggedGroup(null);
      return;
    }
    
    // Handle video dropping to create a new group (existing functionality)
    if (!draggedVideo || !sourceGroupId) return;
    
    // Update groups
    setVideoGroups(prevGroups => {
      const newGroups = [...prevGroups];
      
      // Remove video from source group
      const sourceGroupIndex = newGroups.findIndex(group => group.id === sourceGroupId);
      if (sourceGroupIndex !== -1) {
        newGroups[sourceGroupIndex] = {
          ...newGroups[sourceGroupIndex],
          files: newGroups[sourceGroupIndex].files.filter(file => file.id !== draggedVideo.id)
        };
      }
      
      // Create a new group with the dragged video
      const newGroup = {
        id: newGroups.length + 1,
        files: [draggedVideo],
        similarity: 100
      };
      
      newGroups.push(newGroup);
      
      // Remove empty groups and renumber groups
      const nonEmptyGroups = newGroups.filter(group => group.files.length > 0);
      const renumberedGroups = nonEmptyGroups.map((group, index) => ({
        ...group,
        id: index + 1
      }));
      
      // Update location names to match new group IDs
      const newLocationNames = {...locationNames};
      nonEmptyGroups.forEach((group, index) => {
        const newId = index + 1;
        if (group.id !== newId && locationNames[group.id]) {
          newLocationNames[newId] = locationNames[group.id];
          delete newLocationNames[group.id];
        }
      });
      
      // Initialize location name for the new group
      if (!newLocationNames[newGroup.id]) {
        newLocationNames[newGroup.id] = `Location ${newGroup.id}`;
      }
      
      setLocationNames(newLocationNames);
      return renumberedGroups;
    });
    
    // Reset drag state
    setDraggedVideo(null);
    setSourceGroupId(null);
  };

  // Scroll OCR log to bottom when new entries are added
  useEffect(() => {
    const logElement = document.getElementById('ocr-log');
    if (logElement) {
      logElement.scrollTop = logElement.scrollHeight;
    }
  }, [ocrLog]);

  // Check if all OCR processes are completed but don't trigger grouping automatically
  // Create "no timestamp detected" group for videos without timestamps
  useEffect(() => {
    if (step === 2 && videoFiles.length > 0) {
      // Check if all files have completed OCR processing
      const allOcrCompleted = videoFiles.every(item => ocrCompleted[item.id]);
      
      if (allOcrCompleted) {
        // Separate videos with and without timestamps
        const videosWithTimestamps = [];
        const videosWithoutTimestamps = [];
        
        videoFiles.forEach(video => {
          // Check for OCR extracted timestamps first
          const ocrTimestamps = extractedTimestamps[video.id] || [];
          // Check for manually entered timestamps
          const manualTimestamp = manualStartTimes[video.id];
          
          if (ocrTimestamps.length > 0 || manualTimestamp) {
            videosWithTimestamps.push(video);
          } else {
            videosWithoutTimestamps.push(video);
          }
        });
        
        // Create a special group for videos without timestamps at the beginning
        let specialGroup = null;
        if (videosWithoutTimestamps.length > 0) {
          specialGroup = {
            id: 0, // Special ID for the first group
            files: videosWithoutTimestamps,
            similarity: 0,
            reason: 'No Timestamps Detected',
            noTimestamps: true // Flag to identify this special group
          };
          
          // Update video groups with just the special group for videos without timestamps
          setVideoGroups([specialGroup]);
          
          // Initialize location name for the special group
          if (!locationNames[0]) {
            setLocationNames(prev => ({
              ...prev,
              0: 'Location 0 (No Timestamps)'
            }));
          }
        } else {
          // Clear any existing groups if all videos now have timestamps
          setVideoGroups([]);
        }
        
        if (videoFiles.length === 1 && !groupingCompleted) {
          // Only one video, no grouping needed
          // Only update state if it's not already true to prevent infinite loop
          setGroupingCompleted(true);
          // Initialize location name for single video file
          if (!locationNames[1]) {
            setLocationNames(prev => ({
              ...prev,
              1: 'Location 1'
            }));
          }
          setOcrLog(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: 'Analysis complete. Single file detected, similarity analysis not required.'
          }]);
        }
      }
    }
  }, [ocrCompleted, step, videoFiles.length, groupingCompleted, extractedTimestamps, manualStartTimes]);

  // Enhanced video grouping function with timestamp continuity rule
  const performVideoGrouping = async () => {
    setGroupingInProgress(true);
    
    try {
      // Add log entry
      setOcrLog(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: 'Initializing enhanced content analysis with timestamp continuity check...'
      }]);
      
      // First, separate videos with and without timestamps using unified timestamps
      const videosWithTimestamps = [];
      const videosWithoutTimestamps = [];
      
      videoFiles.forEach(video => {
        // Check for unified timestamps
        const videoTimestamps = unifiedTimestamps[video.id] || [];
        
        if (videoTimestamps.length > 0) {
          videosWithTimestamps.push(video);
        } else {
          videosWithoutTimestamps.push(video);
        }
      });
      
      // Create a special group for videos without timestamps at the beginning
      let specialGroup = null;
      if (videosWithoutTimestamps.length > 0) {
        specialGroup = {
          id: 0, // Special ID for the first group
          files: videosWithoutTimestamps,
          similarity: 0,
          reason: 'No Timestamps Detected',
          noTimestamps: true // Flag to identify this special group
        };
        
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Created special group for ${videosWithoutTimestamps.length} video(s) with no timestamps detected`
        }]);
        
        // For videos with no timestamps, restore their original first-frame thumbnails
        // This ensures that even if OCR sampling changed their thumbnails, we show the correct first frame
        for (const video of videosWithoutTimestamps) {
          // Restore the first-frame thumbnail for this video
          await restoreFirstFrameThumbnail(video.id, video.file, setVideoThumbnails);
        }
      }
      
      // Sort videos with timestamps by their first timestamp to ensure proper sequence processing
      const sortedVideoFiles = [...videosWithTimestamps].sort((a, b) => {
        // Get timestamps for video A from unified timestamps
        const timestampsA = unifiedTimestamps[a.id] || [];
        const firstTimestampA = timestampsA.length > 0 ? timestampsA[0] : null;
        
        // Get timestamps for video B from unified timestamps
        const timestampsB = unifiedTimestamps[b.id] || [];
        const firstTimestampB = timestampsB.length > 0 ? timestampsB[0] : null;
        
        // Handle cases where one or both don't have timestamps
        if (!firstTimestampA) return 1;
        if (!firstTimestampB) return -1;
        
        // Parse first timestamp of each video
        const [datePartA, timePartA] = firstTimestampA.split(' ');
        const [yearA, monthA, dayA] = datePartA.split('-').map(Number);
        const [hourA, minuteA] = timePartA.split(':').map(Number);
        const dateTimeA = new Date(yearA, monthA - 1, dayA, hourA, minuteA);
        
        const [datePartB, timePartB] = firstTimestampB.split(' ');
        const [yearB, monthB, dayB] = datePartB.split('-').map(Number);
        const [hourB, minuteB] = timePartB.split(':').map(Number);
        const dateTimeB = new Date(yearB, monthB - 1, dayB, hourB, minuteB);
        
        return dateTimeA - dateTimeB;
      });
      
      // Create groups based on timestamp continuity first
      const timestampGroups = [];
      const processedVideos = new Set();
      
      // Process videos sequentially to form continuous groups
      for (let i = 0; i < sortedVideoFiles.length; i++) {
        const video = sortedVideoFiles[i];
        
        // Skip if already grouped
        if (processedVideos.has(video.id)) continue;
        
        // Get timestamps for this video from unified timestamps
        const videoTimestamps = unifiedTimestamps[video.id] || [];
        
        // Skip if no timestamps (shouldn't happen here since we filtered)
        if (videoTimestamps.length === 0) {
          processedVideos.add(video.id);
          continue;
        }
        
        // Check if this video can be added to an existing group
        let addedToGroup = false;
        for (const group of timestampGroups) {
          // Check if this video is continuous with the last video in the group
          const lastVideoInGroup = group.files[group.files.length - 1];
          
          // Get timestamps for the last video in group from unified timestamps
          const lastVideoTimestamps = unifiedTimestamps[lastVideoInGroup.id] || [];
          
          if (lastVideoTimestamps.length > 0 && videoTimestamps.length > 0) {
            const isContinuous = checkTimestampContinuity(lastVideoTimestamps, videoTimestamps);
            if (isContinuous) {
              group.files.push(video);
              processedVideos.add(video.id);
              addedToGroup = true;
              
              setOcrLog(prev => [...prev, {
                timestamp: new Date().toLocaleTimeString(),
                message: `Grouped ${video.file.name} with existing group (continuous timestamps)`
              }]);
              
              break;
            }
          }
        }
        
        // If not added to an existing group, create a new group
        if (!addedToGroup) {
          const group = {
            id: timestampGroups.length + (specialGroup ? 1 : 0) + 1, // Adjust ID based on special group
            files: [video],
            similarity: 100,
            reason: 'Timestamp Continuity'
          };
          
          processedVideos.add(video.id);
          timestampGroups.push(group);
          
          setOcrLog(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Created new group for ${video.file.name}`
          }]);
          
          // Initialize location name for the new group
          if (!locationNames[group.id]) {
            setLocationNames(prev => ({
              ...prev,
              [group.id]: `Location ${group.id}`
            }));
          }
        }
      }
      
      // For videos that weren't grouped by timestamp continuity, use visual similarity
      const remainingVideos = videosWithTimestamps.filter(video => !processedVideos.has(video.id));
      
      let similarityGroups = [];
      if (remainingVideos.length > 0) {
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Analyzing ${remainingVideos.length} remaining videos using visual similarity...`
        }]);
        
        // Extract structural features from thumbnails for accurate comparison
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: 'Extracting and normalizing structural features from video thumbnails...'
        }]);
        
        const videoFeatures = [];
        
        // Extract features for each remaining video thumbnail
        for (const item of remainingVideos) {
          if (item && item.file && videoThumbnails[item.id]) {
            try {
              // Create canvas and load image
              const img = new Image();
              img.crossOrigin = 'Anonymous';
              
              // Convert to canvas for processing
              const canvas = document.createElement('canvas');
              canvas.setAttribute('willReadFrequently', 'true');
              const ctx = canvas.getContext('2d');
              
              // Set larger size for better accuracy
              const targetSize = 256;
              canvas.width = targetSize;
              canvas.height = targetSize;
              
              // Load and resize image
              await new Promise((resolve, reject) => {
                img.onload = () => {
                  // Draw resized image to canvas
                  ctx.drawImage(img, 0, 0, targetSize, targetSize);
                  resolve();
                };
                img.onerror = reject;
                img.src = videoThumbnails[item.id];
              });
              
              // Preprocess image to normalize lighting and colors
              setOcrLog(prev => [...prev, {
                timestamp: new Date().toLocaleTimeString(),
                message: `Normalizing lighting and colors for ${item.file.name}...`
              }]);
              
              const processedImageData = preprocessImageForComparison(ctx, targetSize, targetSize);
              
              videoFeatures.push({
                id: item.id,
                file: item,
                imageData: processedImageData,
                canvas: canvas
              });
            } catch (error) {
              // Removed console.error for production
              setOcrLog(prev => [...prev, {
                timestamp: new Date().toLocaleTimeString(),
                message: `Error extracting features for ${item?.file?.name || 'unknown file'}: ${error.message}`
              }]);
            }
          }
        }
        
        // Group remaining videos based on structural similarity
        const processedRemainingVideos = new Set();
        
        // Compare each video with others using SSIM
        for (let i = 0; i < videoFeatures.length; i++) {
          const video1 = videoFeatures[i];
          
          // Skip if already grouped
          if (processedRemainingVideos.has(video1.id)) continue;
          
          // Create a new group for this video
          const group = {
            id: timestampGroups.length + similarityGroups.length + (specialGroup ? 1 : 0) + 1, // Adjust ID based on special group
            files: [video1.file],
            similarity: 100,
            reason: 'Visual Similarity'
          };
          
          processedRemainingVideos.add(video1.id);
          
          setOcrLog(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Analyzing ${video1.file.file.name} for content similarities...`
          }]);
          
          // Initialize location name for the new group
          if (!locationNames[group.id]) {
            setLocationNames(prev => ({
              ...prev,
              [group.id]: `Location ${group.id}`
            }));
          }
          
          // Compare with other videos
          for (let j = i + 1; j < videoFeatures.length; j++) {
            const video2 = videoFeatures[j];
            
            // Skip if already grouped
            if (processedRemainingVideos.has(video2.id)) continue;
            
            try {
              // Compute similarity using SSIM
              const similarity = await computeSSIM(video1.imageData, video2.imageData);
              
              // If similarity is above threshold, group them
              if (similarity > 0.65) { // 65% threshold for grouping
                group.files.push(video2.file);
                processedRemainingVideos.add(video2.id);
                
                // Update group similarity (minimum of all comparisons)
                group.similarity = Math.min(group.similarity, Math.round(similarity * 100));
                
                setOcrLog(prev => [...prev, {
                  timestamp: new Date().toLocaleTimeString(),
                  message: `Grouped ${video1.file.file.name} with ${video2.file.file.name} (${Math.round(similarity * 100)}% visual similarity)`
                }]);
              }
            } catch (error) {
              // Removed console.error for production
            }
          }
          
          similarityGroups.push(group);
        }
        
        // Clean up canvas elements
        videoFeatures.forEach(video => {
          if (video.canvas) {
            video.canvas.remove();
          }
        });
      }
      
      // Combine all groups: special group first, then timestamp groups, then similarity groups
      const allGroups = [];
      if (specialGroup) {
        allGroups.push(specialGroup);
      }
      allGroups.push(...timestampGroups, ...similarityGroups);
      
      // Sort videos within each group by timestamp before setting the state
      const sortedGroups = sortVideosInGroups(allGroups, unifiedTimestamps);
      
      setVideoGroups(sortedGroups);
      
      // Initialize location names for new groups
      const newLocationNames = {...locationNames};
      allGroups.forEach(group => {
        if (!newLocationNames[group.id]) {
          newLocationNames[group.id] = `Location ${group.id}`;
        }
      });
      setLocationNames(newLocationNames);
      
      // Add log entries
      setOcrLog(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `Enhanced analysis complete. Identified ${allGroups.length} group(s) (${specialGroup ? 1 : 0} with no timestamps, ${timestampGroups.length} by timestamp continuity, ${similarityGroups.length} by visual similarity).`
      }]);
      
      allGroups.forEach((group, index) => {
        setOcrLog(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Group ${index + 1}: ${group.files.length} file(s), ${group.similarity || 0}% ${group.reason}`
        }]);
      });
      
      setGroupingCompleted(true);
    } catch (error) {
          // Removed console.error for production
          setOcrLog(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Enhanced content analysis failed: ${error.message}`
          }]);
      setGroupingCompleted(true);
    } finally {
      setGroupingInProgress(false);
    }
  };
  
  // Preprocess image to normalize lighting and colors
  const preprocessImageForComparison = (ctx, width, height) => {
    // Get image data
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    // Step 1: Convert to grayscale using luminance formula
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      
      // Convert to grayscale using luminance formula
      const gray = 0.299 * r + 0.587 * g + 0.114 * b;
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }
    
    // Step 2: Apply histogram equalization for lighting normalization
    applyHistogramEqualization(data, width, height);
    
    // Step 3: Apply Gaussian blur to reduce noise
    applyGaussianBlurForComparison(data, width, height);
    
    // Put the processed image data back to canvas
    ctx.putImageData(imageData, 0, 0);
    
    return ctx.getImageData(0, 0, width, height);
  };
  
  // Apply histogram equalization to normalize lighting
  const applyHistogramEqualization = (data, width, height) => {
    // Calculate histogram
    const hist = new Array(256).fill(0);
    for (let i = 0; i < data.length; i += 4) {
      hist[data[i]]++;
    }
    
    // Calculate cumulative distribution function
    const cdf = new Array(256).fill(0);
    cdf[0] = hist[0];
    for (let i = 1; i < 256; i++) {
      cdf[i] = cdf[i - 1] + hist[i];
    }
    
    // Normalize CDF
    const cdfMin = cdf.find(val => val > 0);
    const totalPixels = width * height;
    
    // Apply histogram equalization
    for (let i = 0; i < data.length; i += 4) {
      const oldVal = data[i];
      const newVal = Math.round(((cdf[oldVal] - cdfMin) / (totalPixels - cdfMin)) * 255);
      data[i] = data[i + 1] = data[i + 2] = newVal;
    }
  };
  
  // Apply Gaussian blur for noise reduction
  const applyGaussianBlurForComparison = (data, width, height) => {
    const output = new Uint8ClampedArray(data.length);
    
    // 3x3 Gaussian kernel
    const kernel = [
      [1, 2, 1],
      [2, 4, 2],
      [1, 2, 1]
    ];
    const kernelWeight = 16;
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let sum = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const nx = Math.min(width - 1, Math.max(0, x + kx));
            const ny = Math.min(height - 1, Math.max(0, y + ky));
            const idx = (ny * width + nx) * 4;
            sum += data[idx] * kernel[ky + 1][kx + 1];
          }
        }
        const idx = (y * width + x) * 4;
        const blurred = Math.round(sum / kernelWeight);
        output[idx] = output[idx + 1] = output[idx + 2] = blurred;
        output[idx + 3] = data[idx + 3]; // Preserve alpha
      }
    }
    
    // Copy output back to data
    for (let i = 0; i < data.length; i++) {
      data[i] = output[i];
    }
  };
  
  // Compute SSIM (Structural Similarity Index) between two images
  const computeSSIM = (img1, img2) => {
    return new Promise((resolve) => {
      try {
        const width = img1.width;
        const height = img1.height;
        const data1 = img1.data;
        const data2 = img2.data;
        
        // SSIM parameters
        const K1 = 0.01;
        const K2 = 0.03;
        const L = 255; // Dynamic range
        const C1 = Math.pow(K1 * L, 2);
        const C2 = Math.pow(K2 * L, 2);
        
        // Convert to grayscale and compute means
        const gray1 = [];
        const gray2 = [];
        
        for (let i = 0; i < data1.length; i += 4) {
          // Convert RGB to grayscale using luminance formula
          const r1 = data1[i];
          const g1 = data1[i + 1];
          const b1 = data1[i + 2];
          const grayVal1 = 0.299 * r1 + 0.587 * g1 + 0.114 * b1;
          gray1.push(grayVal1);
          
          const r2 = data2[i];
          const g2 = data2[i + 1];
          const b2 = data2[i + 2];
          const grayVal2 = 0.299 * r2 + 0.587 * g2 + 0.114 * b2;
          gray2.push(grayVal2);
        }
        
        // Compute local means using sliding window (8x8 windows)
        const windowSize = 8;
        let totalSSIM = 0;
        let windowCount = 0;
        
        for (let y = 0; y <= height - windowSize; y += windowSize) {
          for (let x = 0; x <= width - windowSize; x += windowSize) {
            // Extract window pixels
            const window1 = [];
            const window2 = [];
            
            for (let wy = 0; wy < windowSize; wy++) {
              for (let wx = 0; wx < windowSize; wx++) {
                const idx = (y + wy) * width + (x + wx);
                window1.push(gray1[idx]);
                window2.push(gray2[idx]);
              }
            }
            
            // Compute mean of windows
            let mean1 = 0;
            let mean2 = 0;
            for (let i = 0; i < window1.length; i++) {
              mean1 += window1[i];
              mean2 += window2[i];
            }
            mean1 /= window1.length;
            mean2 /= window2.length;
            
            // Compute variance and covariance
            let var1 = 0;
            let var2 = 0;
            let cov = 0;
            
            for (let i = 0; i < window1.length; i++) {
              const diff1 = window1[i] - mean1;
              const diff2 = window2[i] - mean2;
              var1 += diff1 * diff1;
              var2 += diff2 * diff2;
              cov += diff1 * diff2;
            }
            
            var1 /= (window1.length - 1);
            var2 /= (window1.length - 1);
            cov /= (window1.length - 1);
            
            // Compute SSIM for this window
            const numerator = (2 * mean1 * mean2 + C1) * (2 * cov + C2);
            const denominator = (mean1 * mean1 + mean2 * mean2 + C1) * (var1 + var2 + C2);
            const ssim = numerator / denominator;
            
            totalSSIM += ssim;
            windowCount++;
          }
        }
        
        // Return average SSIM across all windows
        const avgSSIM = windowCount > 0 ? totalSSIM / windowCount : 0;
        
        // Ensure result is between 0 and 1
        const similarity = Math.max(0, Math.min(1, avgSSIM));
        
        resolve(similarity);
      } catch (error) {
        // Removed console.error for production
        resolve(0); // Return 0 similarity on error
      }
    });
  };

  const handleFileChange = (e) => {
    if (e.target && e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (id) => {
    setVideoFiles(prev => prev.filter(item => item && item.id !== id));
    // Also remove timestamps, OCR results, and extracted timestamps for this file
    setTimestamps(prev => {
      const newTimestamps = { ...prev };
      delete newTimestamps[id];
      return newTimestamps;
    });
    setOcrResults(prev => {
      const newOcrResults = { ...prev };
      delete newOcrResults[id];
      return newOcrResults;
    });
    setExtractedTimestamps(prev => {
      const newExtractedTimestamps = { ...prev };
      delete newExtractedTimestamps[id];
      return newExtractedTimestamps;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReportModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      reportMode: mode
    }));
  };

  const handleReportClassChange = (className) => {
    setFormData(prev => ({
      ...prev,
      reportClasses: {
        ...prev.reportClasses,
        [className]: !prev.reportClasses[className]
      }
    }));
  };

  const handleReportIntervalChange = (e) => {
    setFormData(prev => ({
      ...prev,
      reportInterval: parseInt(e.target.value)
    }));
  };

  const goToStep = (stepNum) => {
    // Prevent navigation while grouping is in progress
    if (step === 3 && groupingInProgress) {
      setError('Please wait for content grouping to complete');
      return;
    }
    // Show confirmation when going back to step 1 from any step (except step 1 itself)
    if (stepNum === 1 && step !== 1) {
      setResetTargetStep(1);
      setShowResetConfirmation(true);
      return;
    }
    // Prevent going back to timestamp tab (step 2) from sessions tab (step 3)
    if (step === 3 && stepNum === 2) {
      setError('Navigation to the Timestamps tab is disabled from the Sessions tab');
      return;
    }
    // Prevent going back to timestamp tab (step 2) from report tab (step 4) and beyond, but allow going back to sessions tab (step 3)
    if (step >= 4 && stepNum === 2) {
      setError('Navigation to the Timestamps tab is disabled from the Report tab and beyond');
      return;
    }
    // Check if trying to navigate forward from sessions tab (step 3) to next steps
    if (step === 3 && stepNum > 3) {
      // Check if all durations are confirmed
      if (videoFiles.length > 1 && groupingCompleted) {
        const allConfirmed = videoGroups.every(group => confirmedDurations[group.id]);
        if (!allConfirmed) {
          setError('Please confirm durations for all locations before proceeding');
          return;
        }
      }
    }
    // Check if trying to navigate to a step that requires completion of previous required steps
    if (stepNum > 1) {
      // Files tab (step 1) is required
      if (stepNum > 1 && videoFiles.length === 0) {
        setError('Please select at least one video file to continue');
        return;
      }
      
      // Timestamps tab (step 2) is required
      if (stepNum > 2 && step < 2) {
        setError('Please complete the Timestamps step first');
        return;
      }
      
      // Check if Timestamps step is properly completed
      if (stepNum > 2 && step === 2) {
        // Check if OCR processing is still in progress
        if (Object.values(ocrProcessing).some(Boolean)) {
          setError('Please wait for timestamp analysis to complete');
          return;
        }
        // Check if there are videos without timestamps
        if (videoGroups.some(group => group.noTimestamps && group.files.length > 0)) {
          setError('Please enter timestamps for all videos that require manual input');
          return;
        }
      }
      
      // Sessions tab (step 3) is required
      if (stepNum > 3 && step < 3) {
        setError('Please complete the Sessions step first');
        return;
      }
      
      // Check if Sessions step is properly completed
      if (stepNum > 3 && step === 3) {
        if (videoFiles.length > 1 && !groupingCompleted) {
          setError('Please wait for content grouping to complete');
          return;
        }
        // Check if all durations are confirmed
        if (videoFiles.length > 1 && groupingCompleted) {
          const allConfirmed = videoGroups.every(group => confirmedDurations[group.id]);
          if (!allConfirmed) {
            setError('Please confirm durations for all locations before proceeding');
            return;
          }
        }
      }
    }
    
    // Allow navigation to any step if requirements are met
    setStep(stepNum);
    // Add the new step to visited steps if it's not already there
    if (!visitedSteps.includes(stepNum)) {
      setVisitedSteps(prev => [...prev, stepNum].sort((a, b) => a - b));
    }
    setError('');
  };

  const nextStep = () => {
    if (step === 1 && videoFiles.length === 0) {
      setError('Please select at least one video file');
      return;
    }
    // For step 2 (Timestamps), ensure OCR processing is completed and all timestamps are entered
    if (step === 2 && videoFiles.length > 0) {
      // Check if OCR processing is still in progress
      if (Object.values(ocrProcessing).some(Boolean)) {
        setError('Please wait for timestamp analysis to complete');
        return;
      }
      // Check if there are videos without timestamps
      if (videoGroups.some(group => group.noTimestamps && group.files.length > 0)) {
        setError('Please enter timestamps for all videos without detected timestamps');
        return;
      }
    }
    // For step 3 (Sessions), ensure grouping is completed and all durations are confirmed
    if (step === 3) {
      if (videoFiles.length > 1 && !groupingCompleted) {
        setError('Please wait for content grouping to complete');
        return;
      }
      // Check if all durations are confirmed
      if (videoFiles.length > 1 && groupingCompleted) {
        const allConfirmed = videoGroups.every(group => confirmedDurations[group.id]);
        if (!allConfirmed) {
          setError('Please confirm durations for all locations before proceeding');
          return;
        }
      } else if (videoFiles.length === 1) {
        // For single video file, check if duration is confirmed and directions are set
        const isDurationConfirmed = confirmedDurations[1] || false;
        const isDirectionSet = groupDirectionsSet && groupDirectionsSet[1];
        
        if (!isDurationConfirmed) {
          setError('Please confirm the location duration before proceeding');
          return;
        }
        
        if (!isDirectionSet) {
          setError('Please set directions for the location before proceeding');
          return;
        }
      }
    }
    setStep(prev => Math.min(prev + 1, 5));
    setError('');
  };

  const prevStep = () => {
    // Prevent going back to timestamp tab (step 2) from report tab (step 4) and beyond, but allow going back to sessions tab (step 3)
    if (step === 4) {
      // When on the report tab (step 4), allow going back to sessions tab (step 3)
      setStep(3);
      setError('');
      return;
    } else if (step > 4) {
      // When on summary tab (step 5) or beyond, going back will go to report tab (step 4), which is allowed
      setStep(prev => Math.max(prev - 1, 1));
      setError('');
      return;
    } else if (step === 3) {
      // If user is on step 3 (sessions), going back to step 2 (timestamps) is prevented
      setError('Navigation to the Timestamps tab is disabled from the Sessions tab');
      return;
    }
    // Show confirmation when going back to files tab (step 1) from any step (except step 1 itself)
    if (step !== 1) {
      setResetTargetStep(1);
      setShowResetConfirmation(true);
    } else {
      // This shouldn't happen since we're already on step 1, but just in case
      setStep(prev => Math.max(prev - 1, 1));
    }
  };

  const handleResetConfirmation = (confirmed) => {
    if (confirmed) {
      // Reset all states
      setVideoFiles([]);
      setVideoThumbnails({});
      setThumbnailQueue([]);
      setTimestamps({});
      setUnifiedTimestamps({});
      setOcrResults({});
      setOcrProcessing({});
      setOcrCompleted({});
      setExtractedTimestamps({});
      setOcrRetryCount({});
      setOcrProgress({});
      setOcrLog([]);
      setVideoGroups([]);
      setGroupingInProgress(false);
      setGroupingCompleted(false);
      setLocationNames({});
      setDraggedVideo(null);
      setSourceGroupId(null);
      setDraggedGroup(null);
      setManualTimestampRanges({});
      setVideoDurations({});
      setConfirmedDurations({});
      setAllDurationsConfirmed(false);
      setManualStartTimes({});
      setEnlargedThumbnail(null);
      setTimestampSaved(false);

      // Reset direction and polygon data
      setGroupDirectionsSet({});
      setGroupCardinalDirections({});
      setSavedDrivableAreas({});
      setShowDirectionsErrorOverlay(false);

      // Reset form data
      setFormData({
        location: '',
        reportInterval: 15,
        reportClasses: {
          passengerVehicle: true,
          threeAxelTruck: true,
          bicycle: true,
          pedestrians: true,
          commercialTruck: false,
          bus: false
        }
      });

      // Reset step to target step (should be 1)
      setStep(resetTargetStep);
      setVisitedSteps([1]);
    }

    // Close the confirmation dialog
    setShowResetConfirmation(false);
    setResetTargetStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    // In 'same' mode, we check that all groups have location names
    // In 'separate' mode, we also check that all groups have location names
    let emptyLocationGroups = [];
    if (videoFiles.length > 1) {
      emptyLocationGroups = videoGroups.filter(group => 
        group.id !== 0 && (!locationNames[group.id] || !locationNames[group.id].trim())
      );
    } else if (videoFiles.length === 1) {
      if (!locationNames[1] || !locationNames[1].trim()) {
        emptyLocationGroups = [1];
      }
    }
    
    if (emptyLocationGroups.length > 0) {
      if (videoFiles.length > 1) {
        setError(`Please provide location names for all groups. ${emptyLocationGroups.length} group(s) are missing location names.`);
      } else {
        setError('Please provide a location name for your video.');
      }
      return;
    }
    
    // Check if any file items are missing the file property
    const invalidFiles = videoFiles.filter(item => !item || !item.file);
    if (invalidFiles.length > 0) {
      setError('Some selected files are invalid. Please reselect your files.');
      return;
    }
    
    try {
      setUploading(true);
      setUploadProgress(0); // Initialize progress to 0
      setError('');
      
      // For chunked upload, we'll process one file at a time
      // In a real implementation, you might want to process multiple files in parallel
      const results = [];
      
      // Create a map to store grouping IDs for each group
      const groupIds = {};
      
      for (const videoItem of videoFiles) {
        if (videoItem && videoItem.file) {
          const file = videoItem.file;
          
          // Get video duration and round to nearest minute
          let videoDuration = null;
          try {
            const rawDuration = await getVideoDuration(file);
            // Round to nearest minute
            videoDuration = Math.round(rawDuration / 60);
          } catch (durationError) {
            console.error('Failed to get video duration:', durationError);
          }
          
          // Find which group this file belongs to
          let groupLocation = formData.location; // Default to form location
          let groupReportInterval = formData.reportInterval; // Default to form report interval
          let groupId = null;
          
          // Get polygon and cardinal direction data for this group (default to null if not found)
          let groupPolygon = null;
          let groupCardinalDirectionsData = null;
          
          // Check if we're in separate mode and find the specific group configuration
          if (formData.reportMode === 'separate') {
            if (videoFiles.length > 1) {
              for (const group of videoGroups) {
                if (group.files && group.files.some(f => f.id === videoItem.id)) {
                  // Use the group's name as the location
                  groupLocation = locationNames[group.id] || `Location ${group.id}`;
                  // Use the group's specific report interval if available
                  const groupConfig = separateConfigurations[group.id];
                  if (groupConfig && groupConfig.reportInterval) {
                    groupReportInterval = groupConfig.reportInterval;
                  }
                  // Get or create grouping ID for this group
                  if (!groupIds[group.id]) {
                    groupIds[group.id] = 'group_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                  }
                  groupId = groupIds[group.id];
                  
                  // Get polygon and cardinal direction data for this group
                  groupPolygon = savedDrivableAreas[group.id] || null;
                  groupCardinalDirectionsData = groupCardinalDirections[group.id] || null;
                  
                  break;
                }
              }
            } else if (videoFiles.length === 1) {
              // For single video file
              groupLocation = locationNames[1] || 'Location 1';
              // Get or create grouping ID for this group
              if (!groupIds[1]) {
                groupIds[1] = 'group_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
              }
              groupId = groupIds[1];
              
              // Get polygon and cardinal direction data for this group
              groupPolygon = savedDrivableAreas[1] || null;
              groupCardinalDirectionsData = groupCardinalDirections[1] || null;
            }
          } else {
            // In 'same' mode, still use the group name as location but keep the shared report interval
            if (videoFiles.length > 1) {
              for (const group of videoGroups) {
                if (group.files && group.files.some(f => f.id === videoItem.id)) {
                  // Use the group's name as the location
                  groupLocation = locationNames[group.id] || `Location ${group.id}`;
                  // Get or create grouping ID for this group
                  if (!groupIds[group.id]) {
                    groupIds[group.id] = 'group_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                  }
                  groupId = groupIds[group.id];
                  
                  // Get polygon and cardinal direction data for this group
                  groupPolygon = savedDrivableAreas[group.id] || null;
                  groupCardinalDirectionsData = groupCardinalDirections[group.id] || null;
                  
                  break;
                }
              }
            } else if (videoFiles.length === 1) {
              // For single video file
              groupLocation = locationNames[1] || 'Location 1';
              // Get or create grouping ID for this group
              if (!groupIds[1]) {
                groupIds[1] = 'group_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
              }
              groupId = groupIds[1];
              
              // Get polygon and cardinal direction data for this group
              groupPolygon = savedDrivableAreas[1] || null;
              groupCardinalDirectionsData = groupCardinalDirections[1] || null;
            }
          }
          
          // If we couldn't find a group (shouldn't happen), create a default grouping ID
          if (!groupId) {
            groupId = 'group_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
          }
          
          // Create a video processing payment intent for this specific video (skip for admin users)
          let paymentIntentClientSecret = null;
          let paymentIntentId = null;

          // Check if current user is admin - if so, skip payment creation
          if (currentUser?.isAdmin) {
            // For admin users, we create mock payment intent data
            paymentIntentClientSecret = `pi_admin_secret_${Date.now()}_${currentUser.id || 'admin'}`;
            paymentIntentId = `pi_admin_${Date.now()}_${currentUser.id || 'admin'}`;
          } else {
            try {
              // Prepare payment data for video processing
              const videoProcessingCost = 5.00; // $5.00 CAD per video processing (or calculate based on video size/duration)
              const paymentDescription = `Video processing for ${file.name}`;

              const videoProcessingDetails = [{
                videoId: videoItem.id,
                fileName: file.name,
                fileSize: file.size,
                duration: videoDuration || 0
              }];

              const paymentData = {
                amount: videoProcessingCost,
                description: paymentDescription,
                videoProcessingDetails: videoProcessingDetails
              };
              // Use authAPI to call the payment intent endpoint since it goes through API gateway
              const paymentResponse = await authAPI.createPaymentIntentForVideoProcessing(paymentData);

              if (paymentResponse.data && paymentResponse.data.clientSecret) {
                paymentIntentClientSecret = paymentResponse.data.clientSecret;
                paymentIntentId = paymentResponse.data.paymentIntentId;
              } else {
                throw new Error(`Payment intent creation failed for ${file.name}`);
              }
            } catch (paymentError) {
              setError(`Failed to create payment for ${file.name}: ${paymentError.message}`);
              throw paymentError;
            }
          }

          // Prepare metadata for this file, now including payment intent information
          const metadata = {
            location: groupLocation,
            reportInterval: groupReportInterval,
            reportClasses: formData.reportClasses,
            groupingId: groupId,
            polygon: groupPolygon,
            cardinalDirections: groupCardinalDirectionsData,
            duration: videoDuration, // Add video duration to metadata
            videoTimestamp: getVideoTimestampForFile(videoItem.id), // Add video timestamp to metadata
            paymentIntentId: paymentIntentId, // Add payment intent ID to metadata
            paymentClientSecret: paymentIntentClientSecret // Add payment client secret to metadata
          };

          // Upload file using chunked upload
          const result = await uploadFileInChunks(
            file,
            metadata,
            (progress) => {
              // Update progress for this file
              // Calculate overall progress: (completed files * 100 + current file progress) / total files
              const fileProgress = Math.round((results.length * 100 + progress) / videoFiles.length);
              setUploadProgress(fileProgress);
            },
            (errorMessage) => {
              console.error(`Error uploading file ${file.name}:`, errorMessage);
              setError(errorMessage);
            }
          );
          results.push(result);
        }
      }
      
      toast.success('Upload Successful!', {
        description: 'Video(s) uploaded successfully! Redirecting to dashboard...',
      });
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Upload failed with error:', err);
      console.error('Error stack:', err.stack);
      setError('Failed to upload video(s). Please try again. Error: ' + (err.message || 'Unknown error'));
      // Reset upload progress on error
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  // Required steps
  const requiredSteps = [1, 2, 3]; // Files, Timestamps, and Sessions are required

  // Function to open enlarged thumbnail modal
  const openEnlargedThumbnail = (videoItem, groupId) => {
    setEnlargedThumbnail({
      video: videoItem,
      groupId: groupId,
      startTime: '' // Initialize empty start time
    });
  };
  
  // Function to close enlarged thumbnail modal
  const closeEnlargedThumbnail = () => {
    setEnlargedThumbnail(null);
  };
  
  // Function to save timestamp from enlarged thumbnail modal
  const saveTimestampFromModal = () => {
    if (!enlargedThumbnail) return;
    
    const { video, groupId, startTime } = enlargedThumbnail;
    
    // Validate the start time format (YYYY-MM-DD HH:MM)
    const timeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
    if (!timeRegex.test(startTime)) {
      setOcrLog(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `Invalid timestamp format for ${video.file.name}. Expected: YYYY-MM-DD HH:MM`
      }]);
      return;
    }
    
    // Parse the timestamp
    const [datePart, timePart] = startTime.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    const timestamp = new Date(year, month - 1, day, hour, minute);
    
    // Validate the date range
    const currentYear = new Date().getFullYear();
    const minDate = new Date(1960, 0, 1);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999);
    
    if (timestamp < minDate || timestamp > tomorrow) {
      setOcrLog(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `Invalid date for ${video.file.name}. Must be between 1960 and tomorrow.`
      }]);
      return;
    }
    
    // Save the manual start time
    if (groupId) {
      // For videos in groups (no timestamps group)
      setManualStartTimes(prev => ({
        ...prev,
        [groupId]: startTime
      }));
      
      // Add video to appropriate group based on timestamp and remove from "no timestamps" group
      addToGroupBasedOnTimestamp(video, startTime, videoGroups, setVideoGroups, extractedTimestamps, manualStartTimes, setTimestamps, setOcrLog, unifiedTimestamps);
    } else {
      // For regular video widgets, store with video ID
      setManualStartTimes(prev => ({
        ...prev,
        [video.id]: startTime
      }));
      
      // Add video to appropriate group based on timestamp
      addToGroupBasedOnTimestamp(video, startTime, videoGroups, setVideoGroups, extractedTimestamps, manualStartTimes, setTimestamps, setOcrLog, unifiedTimestamps);
    }
    
    setOcrLog(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message: `Manual start time saved for ${video.file.name}: ${startTime}`
    }]);
    
    // Show success animation
    setTimestampSaved(true);
    
    // Show success toast
    toast.success('Timestamp saved successfully!', {
      description: `Video ${video.file.name} has been assigned timestamp ${startTime}`,
    });
    
    // Close the modal after a short delay to show the success animation
    setTimeout(() => {
      closeEnlargedThumbnail();
      setTimestampSaved(false); // Reset for next time
    }, 1500);
  };
  
  // Function to handle start time change in modal
  const handleModalStartTimeChange = (value) => {
    if (!enlargedThumbnail) return;
    
    setEnlargedThumbnail(prev => ({
      ...prev,
      startTime: value
    }));
  };
  
  // Function to get video timestamp for a specific file
  const getVideoTimestampForFile = (fileId) => {
    // Check if there's an extracted timestamp for this file from OCR
    if (extractedTimestamps[fileId] && extractedTimestamps[fileId].length > 0) {
      // Use the first extracted timestamp (multiple timestamps may exist for different moments in the video)
      const extractedTimestamp = extractedTimestamps[fileId][0];
      // Extract the date and time from the OCR timestamp
      if (extractedTimestamp && typeof extractedTimestamp === 'object' && extractedTimestamp.time) {
        // If extractedTimestamp is an object with time field
        return extractedTimestamp.time;
      } else if (typeof extractedTimestamp === 'string') {
        // If it's a string timestamp
        return extractedTimestamp;
      }
    }

    // Check if there's a manual start time for this file
    if (manualStartTimes[fileId]) {
      // If manualStartTimes is stored as string in format "YYYY-MM-DD HH:MM"
      if (typeof manualStartTimes[fileId] === 'string') {
        const timeStr = manualStartTimes[fileId];
        // Convert to proper date format for backend if needed
        return timeStr;
      } else if (manualStartTimes[fileId] instanceof Date) {
        return manualStartTimes[fileId].toISOString();
      }
    }

    // For grouped videos, check if there's a timestamp associated with the group
    if (videoGroups.length > 0) {
      for (const group of videoGroups) {
        const fileInGroup = group.files?.find(f => f.id === fileId);
        if (fileInGroup) {
          // Check if this group has timestamps
          if (extractedTimestamps[fileId] && extractedTimestamps[fileId].length > 0) {
            return extractedTimestamps[fileId][0];
          }
        }
      }
    }

    // If no timestamp found, return null
    return null;
  };

  // Function to update location name
  const updateLocationName = (groupId, newName) => {
    setLocationNames(prev => ({
      ...prev,
      [groupId]: newName
    }));
  };

  return (
    <div className="container mx-auto py-8 relative">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI-Powered Video Analysis</h1>
          <p className="text-gray-600">Upload your video files for intelligent traffic analysis and reporting</p>
        </div>
        
        {/* Scroll Indicators */}
        <div 
          id="scroll-top-indicator"
          className="fixed top-0 left-0 right-0 bg-blue-600 text-white text-center py-3 z-50 pointer-events-none rounded-b-lg shadow-lg opacity-0"
          style={{ display: 'none' }}
        >
          <div className="flex items-center justify-center">
            <ChevronUp className="h-6 w-6 mr-3 animate-bounce" />
            <span className="text-lg font-semibold">Scrolling Up - Release to scroll</span>
            <ChevronUp className="h-6 w-6 ml-3 animate-bounce" />
          </div>
        </div>
        <div 
          id="scroll-bottom-indicator"
          className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white text-center py-3 z-50 pointer-events-none rounded-t-lg shadow-lg opacity-0"
          style={{ display: 'none' }}
        >
          <div className="flex items-center justify-center">
            <ChevronDown className="h-6 w-6 mr-3 animate-bounce" />
            <span className="text-lg font-semibold">Scrolling Down - Release to scroll</span>
            <ChevronDown className="h-6 w-6 ml-3 animate-bounce" />
          </div>
        </div>
        
        <UploadStepper 
          steps={steps} 
          currentStep={step} 
          visitedSteps={visitedSteps} 
          videoFilesLength={videoFiles.length} 
          onStepChange={goToStep} 
          groupingInProgress={groupingInProgress} 
        />
        
        <Card className="shadow-lg">
          <CardContent className="p-6">
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Reset Confirmation Dialog */}
            {showResetConfirmation && (
              <Alert className="mb-6 bg-yellow-50 border-yellow-200">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  <div className="flex flex-col space-y-4">
                    <p className="font-medium">Reset Confirmation</p>
                    <p>Going back to the files tab will reset all your progress. Are you sure you want to continue?</p>
                    <div className="flex space-x-3">
                      <Button 
                        variant="outline" 
                        onClick={() => handleResetConfirmation(false)}
                        className="border-yellow-300 text-yellow-700 hover:bg-yellow-100"
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={() => handleResetConfirmation(true)}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        Reset Progress
                      </Button>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            {/* Directions Error Overlay */}
            {showDirectionsErrorOverlay && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999999]">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-red-600">Directions Required</h3>
                    <button 
                      onClick={() => setShowDirectionsErrorOverlay(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="mb-6">
                    <p className="text-gray-700 mb-4">
                      Please set directions for all locations before confirming durations.
                    </p>
                    <p className="text-gray-600 text-sm">
                      Click the "Set" button for each location group to define the drivable area.
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <Button 
                      onClick={() => setShowDirectionsErrorOverlay(false)}
                      className="px-4 py-2"
                    >
                      OK
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} id="upload-form">
              {/* Step 1: File Selection */}
              {step === 1 && (
                <FileSelectionStep
                  videoFiles={videoFiles}
                  processingFiles={processingFiles}
                  videoThumbnails={videoThumbnails}
                  thumbnailQueue={thumbnailQueue}
                  dragActive={dragActive}
                  handleDrag={handleDrag}
                  handleDrop={handleDrop}
                  handleFileChange={handleFileChange}
                  uploading={uploading}
                  openEnlargedThumbnail={openEnlargedThumbnail}
                  removeFile={removeFile}
                  nextStep={nextStep}
                  onRemoveAll={() => {
                    // Show confirmation when removing all files
                    setResetTargetStep(1);
                    setShowResetConfirmation(true);
                  }}
                />
              )}

              {/* Step 2: Timestamps */}
              {step === 2 && (
                <TimestampDetectionStep
                  videoFiles={videoFiles}
                  ocrProcessing={ocrProcessing}
                  ocrCompleted={ocrCompleted}
                  ocrProgress={ocrProgress}
                  extractedTimestamps={extractedTimestamps}
                  videoGroups={videoGroups}
                  openEnlargedThumbnail={openEnlargedThumbnail}
                  prevStep={prevStep}
                  nextStep={nextStep}
                  groupingInProgress={groupingInProgress}
                />
              )}
              
              {/* Step 3: AI-Powered Session Organization */}
              {step === 3 && (
                              <SessionOrganizationStep
                videoFiles={videoFiles}
                groupingCompleted={groupingCompleted}
                groupingInProgress={groupingInProgress}
                videoGroups={videoGroups}
                locationNames={locationNames}
                updateLocationName={updateLocationName}
                videoThumbnails={videoThumbnails}
                manualTimestampRanges={manualTimestampRanges}
                setManualTimestampRanges={setManualTimestampRanges}
                videoDurations={videoDurations}
                confirmedDurations={confirmedDurations}
                setConfirmedDurations={setConfirmedDurations}
                allDurationsConfirmed={allDurationsConfirmed}
                confirmAllDurations={confirmAllDurations}
                handleGroupDragOver={handleGroupDragOver}
                handleGroupDragLeave={handleGroupDragLeave}
                handleGroupDrop={handleGroupDrop}
                handleNewGroupDrop={handleNewGroupDrop}
                handleGroupDragStart={handleGroupDragStart}
                handleDragEnd={handleDragEnd}
                handleVideoDragStart={handleVideoDragStart}
                openEnlargedThumbnail={openEnlargedThumbnail}
                prevStep={prevStep}
                nextStep={nextStep}
                getGroupTimestampRange={(group) => getGroupTimestampRange(group, manualTimestampRanges, unifiedTimestamps, videoDurations)}
                getGroupTotalDuration={(group) => getGroupTotalDuration(group, videoDurations, setVideoDurations)}
                formatDuration={formatDuration}
                getTotalDuration={() => getTotalDuration(videoGroups, videoDurations)}
                getOverallTimestampRange={() => getOverallTimestampRange(videoGroups, unifiedTimestamps, videoDurations)}
                onPerformVideoGrouping={performVideoGrouping}
                ocrCompleted={ocrCompleted}
                onError={setError}
                groupDirectionsSet={groupDirectionsSet}
                setGroupDirectionsSet={setGroupDirectionsSet}
                unifiedTimestamps={unifiedTimestamps}
                groupCardinalDirections={groupCardinalDirections}
                setGroupCardinalDirections={setGroupCardinalDirections}
                savedDrivableAreas={savedDrivableAreas}
                setSavedDrivableAreas={setSavedDrivableAreas}
                />
              )}
              
              {/* Step 4: AI-Powered Report Configuration */}
              {step === 4 && (
                <ReportConfigurationStep
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleReportClassChange={handleReportClassChange}
                  handleReportModeChange={handleReportModeChange}
                  videoGroups={videoGroups}
                  unifiedTimestamps={unifiedTimestamps}
                  videoThumbnails={videoThumbnails}
                  prevStep={prevStep}
                  nextStep={nextStep}
                  separateConfigurations={separateConfigurations}
                  setSeparateConfigurations={setSeparateConfigurations}
                />
              )}
              
              {/* Step 5: AI Processing Summary */}
              {step === 5 && (
                <ProcessingSummaryStep
                  videoFiles={videoFiles}
                  formData={formData}
                  videoGroups={videoGroups}
                  separateConfigurations={separateConfigurations}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  prevStep={prevStep}
                  handleSubmit={handleSubmit}
                  onStepChange={goToStep}
                />
              )}
            </form>
            
            {/* Enlarged Thumbnail Modal */}
            <TimestampModal
              enlargedThumbnail={enlargedThumbnail}
              closeEnlargedThumbnail={closeEnlargedThumbnail}
              videoThumbnails={videoThumbnails}
              handleModalStartTimeChange={handleModalStartTimeChange}
              saveTimestampFromModal={saveTimestampFromModal}
              timestampSaved={timestampSaved}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default UploadPage;